import { Component, HostListener, NgZone, OnDestroy, OnInit } from "@angular/core";
import { MatBottomSheet } from "@angular/material";
import * as _ from 'lodash';
import { ApiService } from "../../services/api.service";
import { AlertService, MessageSeverity } from "../../services/alert.service";
import { LearnMoreContentSheetComponent } from "../whitelabels/whitelabel-configs/learn-more-content/learn-more-content-sheet";
import { BenefitIconSheetComponent } from "./benefit-icon/benefit-icon-sheet";
import { EditDefaultSheetComponent } from "../whitelabels/whitelabel-configs/edit-default/edit-default-sheet";

/**
 * The benefits that render as a card: their icon, the heading on the card, the Learn More
 * copy, and the order the cards are shown in.
 *
 * This deliberately does not live under a whitelabel. Everything here is stored once per
 * feature and used by every site offering that benefit, so editing it from inside one
 * site's config dialog implied a per-site edit that does not exist.
 */
@Component({
    selector: 'benefit-contents',
    template: require('./benefit-contents.component.html'),
    styles: [require('./benefit-contents.component.css')]
})
export class BenefitContentsComponent implements OnInit, OnDestroy {

    features: any[] = [];
    loading = false;
    filter = '';

    /** Row being dragged, and the row it is currently over, as indexes into features. */
    dragIndex: number = null;
    dropIndex: number = null;
    savingOrder = false;

    /** How close to the top or bottom of the window a drag has to get to scroll it. */
    private static readonly EDGE_ZONE_PX = 90;

    /** Most the window moves per frame, reached at the very edge of the zone. */
    private static readonly MAX_STEP_PX = 18;

    private pointerY = 0;
    private autoScrollTimer: any = null;
    private overRow = false;

    constructor(private apiService: ApiService,
                private alertService: AlertService,
                private zone: NgZone,
                private bottomSheet: MatBottomSheet) { }

    ngOnInit() {
        this.load();
    }

    ngOnDestroy() {
        this.stopAutoScroll();
    }

    load() {
        this.loading = true;
        this.apiService.getWhitelabelFeatures().subscribe(r => {
            const all = r || [];

            _.each(all, (f: any) => {
                try {
                    f.defaultSettingsObj = JSON.parse(f.defaultSettings);
                } catch (e) {
                    f.defaultSettingsObj = {};
                }
            });

            // Only benefits that render as a card can show a Learn More link.
            const cards = _.filter(all, (f: any) => !!f.defaultSettingsObj.buttonFunction);

            // Stored order first; benefits with no position yet fall in alphabetically
            // behind the ones that have one.
            this.features = _.sortBy(cards, [
                (f: any) => this.storedOrder(f),
                (f: any) => (f.featureName || '').toLowerCase()
            ]);

            this.loading = false;
        }, () => this.loading = false);
    }

    get visibleFeatures(): any[] {
        const q = (this.filter || '').trim().toLowerCase();
        if (!q) { return this.features; }
        return _.filter(this.features, (f: any) =>
            (f.featureName || '').toLowerCase().indexOf(q) > -1 ||
            ((f.defaultSettingsObj && f.defaultSettingsObj.heading) || '').toLowerCase().indexOf(q) > -1);
    }

    get withContentCount(): number {
        return _.filter(this.features, (f: any) => f.hasLearnMoreContent).length;
    }

    /**
     * Re-ordering is only offered on the unfiltered list: the rows on screen are then the
     * whole list in order, so a dropped row's position is the position that gets saved.
     * Moving a row within a filtered subset has no single sensible answer.
     */
    get canReorder(): boolean {
        return !(this.filter || '').trim();
    }

    /** Held off while a save is in flight so a second move cannot race the first. */
    get dragEnabled(): boolean {
        return this.canReorder && !this.savingOrder;
    }

    onDragStart(event: any, index: number) {
        if (!this.dragEnabled) { return; }

        this.dragIndex = index;
        this.pointerY = event.clientY;

        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            // Firefox will not start a drag unless something is on the transfer.
            event.dataTransfer.setData('text/plain', String(index));
        }

        this.startAutoScroll();
    }

    onDragOver(event: any, index: number) {
        if (this.dragIndex === null) { return; }

        // Without this the browser rejects the drop.
        event.preventDefault();

        if (event.dataTransfer) { event.dataTransfer.dropEffect = 'move'; }
        this.dropIndex = index;

        // Read by the document handler, which runs after this one as the event bubbles.
        this.overRow = true;
    }

    onDrop(event: any, index: number) {
        event.preventDefault();

        const from = this.dragIndex;
        this.clearDrag();

        if (from === null || from === index) { return; }

        const moved = this.features.splice(from, 1)[0];
        this.features.splice(index, 0, moved);

        this.saveOrder();
    }

    onDragEnd() {
        this.clearDrag();
    }

    private clearDrag() {
        this.dragIndex = null;
        this.dropIndex = null;
        this.overRow = false;
        this.stopAutoScroll();
    }

    /**
     * The list runs well past one screen, and a browser ignores the wheel while a native
     * drag is in progress, so the page has to be scrolled for the admin: hold the row near
     * the top or bottom of the window and it creeps that way, faster the closer you hold
     * it. Bound on the document rather than the rows so it keeps working once the pointer
     * is past the last row or over the page header.
     */
    @HostListener('document:dragover', ['$event'])
    onDocumentDragOver(event: any) {
        if (this.dragIndex === null) { return; }

        this.pointerY = event.clientY;

        // Clear of the rows, so drop the indicator: dropping out here moves nothing, and
        // a line left behind on the last row hovered would promise otherwise.
        if (!this.overRow) { this.dropIndex = null; }
        this.overRow = false;

        // Marks the whole page as somewhere the row may be dropped; without it the cursor
        // reads "no drop" everywhere outside a row and the drag looks broken.
        event.preventDefault();
    }

    /** Dropped clear of the list: nothing moves, and the browser must not act on it. */
    @HostListener('document:drop', ['$event'])
    onDocumentDrop(event: any) {
        if (this.dragIndex === null) { return; }
        event.preventDefault();
        this.clearDrag();
    }

    private startAutoScroll() {
        if (this.autoScrollTimer !== null) { return; }

        // Outside Angular: this only moves the window, and running change detection on
        // every frame of a drag would be pure waste.
        this.zone.runOutsideAngular(() => {
            this.autoScrollTimer = window.setInterval(() => this.autoScrollStep(), 16);
        });
    }

    private stopAutoScroll() {
        if (this.autoScrollTimer === null) { return; }
        window.clearInterval(this.autoScrollTimer);
        this.autoScrollTimer = null;
    }

    private autoScrollStep() {
        if (this.dragIndex === null) { this.stopAutoScroll(); return; }

        const edge = BenefitContentsComponent.EDGE_ZONE_PX;
        const fromTop = this.pointerY;
        const fromBottom = window.innerHeight - this.pointerY;

        if (fromTop < edge) {
            window.scrollBy(0, -this.stepFor(edge - fromTop));
        } else if (fromBottom < edge) {
            window.scrollBy(0, this.stepFor(edge - fromBottom));
        }
    }

    /** Depth into the edge zone, in pixels, turned into a speed. */
    private stepFor(depth: number): number {
        const edge = BenefitContentsComponent.EDGE_ZONE_PX;
        const fraction = Math.min(depth, edge) / edge;
        return Math.max(1, Math.round(fraction * BenefitContentsComponent.MAX_STEP_PX));
    }

    /**
     * Where the benefit sits today. sortOrder is what dragging writes; index is the older
     * per-site value the portal ordered cards by before this page existed, and reading it
     * as a fallback means the list opens in the order members already see rather than
     * alphabetically. Coerced because the raw settings editor writes values as text.
     */
    private storedOrder(feature: any): number {
        const settings = feature.defaultSettingsObj || {};

        const stored = settings.sortOrder === null || settings.sortOrder === undefined || settings.sortOrder === ''
            ? settings.index
            : settings.sortOrder;

        const position = Number(stored);

        // Sorts behind every stored position, however many benefits there come to be.
        return isNaN(position) ? 999999 : position;
    }

    /**
     * Saved as soon as a row is dropped -- there is no Save button to forget. On failure
     * the list is re-read so what is on screen is what is stored.
     */
    private saveOrder() {
        const ids = _.map(this.features, (f: any) => f.whitelabelFeatureId);

        this.savingOrder = true;

        this.apiService.saveFeatureOrder(ids).subscribe((r: any) => {
            this.savingOrder = false;

            // Keep the local copy in step so a later filter-and-sort does not undo the move.
            _.each(this.features, (f: any, i: number) => {
                if (f.defaultSettingsObj) { f.defaultSettingsObj.sortOrder = i; }
            });

            if (r && r.unreadable && r.unreadable.length) {
                this.alertService.showMessage("",
                    "Order saved. " + r.unreadable.join(', ') + " could not be included -- " +
                    "those benefits have settings that are not valid JSON.",
                    MessageSeverity.warn);
            }

            if (r && r.keptCustomSites) {
                this.alertService.showMessage("",
                    r.keptCustomSites + " site(s) have their own tile order set in Whitelabels and were left as they are.",
                    MessageSeverity.info);
            }
        }, () => {
            this.savingOrder = false;
            this.alertService.showMessage("",
                "The new order could not be saved.", MessageSeverity.error);
            this.load();
        });
    }

    edit(feature: any) {
        const ref = this.bottomSheet.open(LearnMoreContentSheetComponent, {
            data: { whitelabelFeature: feature }
        });

        // hasLearnMoreContent is computed server-side, so re-read it after an edit
        ref.afterDismissed().subscribe(changed => {
            if (changed) { this.load(); }
        });
    }

    /**
     * The rest of the card's default settings -- button label, link, the copy on the tile.
     * The same editor the Whitelabels dialog opens, because the values are the same ones:
     * what every site starts from, not this or that site's override.
     */
    editDefaults(feature: any) {
        const ref = this.bottomSheet.open(EditDefaultSheetComponent, {
            data: { whitelabelFeature: feature }
        });

        // The save rewrites defaultSettings server-side, so re-read rather than patching
        // the row locally.
        ref.afterDismissed().subscribe(changed => {
            if (changed) { this.load(); }
        });
    }

    editIcon(feature: any) {
        const ref = this.bottomSheet.open(BenefitIconSheetComponent, {
            data: { whitelabelFeature: feature }
        });

        // The save rewrites defaultSettings server-side, so re-read rather than patching
        // the row locally.
        ref.afterDismissed().subscribe(changed => {
            if (changed) { this.load(); }
        });
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/components/benefit-contents/benefit-contents.component.ts